import React from 'react';
import { Skeleton, SkeletonText } from '../../../components/ui/SkeletonLoader';

export const AnalysisDetailSkeleton = () => (
  <div className="space-y-6 md:space-y-8 animate-pulse">
    {/* Property Header Skeleton */}
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex flex-col lg:flex-row">
        <Skeleton className="w-full lg:w-80 h-52 rounded-none flex-shrink-0" />
        <div className="p-5 flex-1 space-y-4">
          <Skeleton className="h-7 w-3/4" />
          <Skeleton className="h-5 w-1/2" />
          <div className="flex gap-3">
            <Skeleton className="h-8 w-20 rounded-full" />
            <Skeleton className="h-8 w-20 rounded-full" />
            <Skeleton className="h-8 w-20 rounded-full" />
          </div>
          <Skeleton className="h-6 w-1/3" />
        </div>
      </div>
    </div>

    {/* Quick Verdict Skeleton */}
    <div className="bg-card border border-border rounded-xl p-5 space-y-4">
      <div className="flex items-center gap-3">
        <Skeleton className="w-10 h-10 rounded-full" />
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-6 w-20 rounded-full ml-auto" />
      </div>
      <SkeletonText lines={4} />
    </div>

    {/* Strengths Skeleton */}
    <div className="bg-card border border-border rounded-xl p-5 space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <Skeleton className="w-8 h-8 rounded-full" />
        <Skeleton className="h-6 w-32" />
      </div>
      {[1, 2, 3, 4]?.map(i => (
        <div key={i} className="flex items-start gap-3">
          <Skeleton className="w-5 h-5 rounded-full flex-shrink-0 mt-0.5" />
          <Skeleton className="h-4 flex-1" />
        </div>
      ))}
    </div>

    {/* Risks Skeleton */}
    <div className="bg-card border border-border rounded-xl p-5 space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <Skeleton className="w-8 h-8 rounded-full" />
        <Skeleton className="h-6 w-28" />
      </div>
      {[1, 2, 3]?.map(i => (
        <div key={i} className="flex items-start gap-3">
          <Skeleton className="w-5 h-5 rounded-full flex-shrink-0 mt-0.5" />
          <Skeleton className="h-4 flex-1" />
        </div>
      ))}
    </div>

    {/* Offer Band Skeleton */}
    <div className="bg-card border border-border rounded-xl p-5 space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <Skeleton className="w-8 h-8 rounded-full" />
        <Skeleton className="h-6 w-36" />
      </div>
      <div className="flex gap-4">
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-8 w-32" />
        </div>
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-8 w-32" />
        </div>
      </div>
      <Skeleton className="h-3 w-full rounded-full" />
    </div>
  </div>
);

export const AnalysisSpinner = () => (
  <div className="flex flex-col items-center justify-center py-24 gap-5">
    <div className="relative">
      <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
      <div className="absolute inset-0 flex items-center justify-center">
        <svg className="w-6 h-6 text-primary animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
        </svg>
      </div>
    </div>
    <div className="text-center">
      <p className="text-base font-semibold text-foreground">Loading Analysis Report</p>
      <p className="text-sm text-muted-foreground mt-1">Preparing your AI-powered insights...</p>
    </div>
  </div>
);

export const AnalysisEmptyState = ({ onStartAnalysis }) => (
  <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
    <div className="relative mb-6">
      <div className="w-28 h-28 rounded-full bg-primary/10 flex items-center justify-center">
        <svg className="w-14 h-14 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
        </svg>
      </div>
      <div className="absolute -bottom-1 -right-1 w-10 h-10 rounded-full bg-cyan-500/20 border-2 border-background flex items-center justify-center">
        <svg className="w-5 h-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      </div>
    </div>
    <h3 className="text-2xl font-semibold text-foreground mb-3">No Analysis Data</h3>
    <p className="text-sm text-muted-foreground max-w-sm mb-2">
      This analysis report could not be loaded. The data may have expired or the analysis was not completed.
    </p>
    <p className="text-xs text-muted-foreground mb-8">Start a new analysis from your dashboard to get AI-powered property insights.</p>
    {onStartAnalysis && (
      <button
        onClick={onStartAnalysis}
        className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
        </svg>
        Start Your First Analysis
      </button>
    )}
  </div>
);

export default AnalysisDetailSkeleton;
